import React from "react";
import { Link } from "react-router-dom";
import { Cloud } from "lucide-react";
import { motion } from "framer-motion";

import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageToggle } from "@/components/auth/LanguageToggle";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";

interface PublicLayoutProps {
  children: React.ReactNode;
}

export function PublicLayout({ children }: PublicLayoutProps) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <nav className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="group flex items-center gap-2">
            <Cloud className="h-8 w-8 text-primary transition-transform group-hover:scale-110" />
            <span className="text-xl font-bold tracking-tight">Cloudface</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <ThemeToggle className="static" />
            <Link to="/login">
              <Button size="sm">{t("sign_in")}</Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="flex-1">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-10 lg:px-8 lg:py-12"
        >
          {children}
        </motion.div>
      </main>

      <footer className="border-t bg-muted/30 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="col-span-2">
              <div className="mb-4 flex items-center gap-2">
                <Cloud className="h-6 w-6" />
                <span className="font-bold">Cloudface</span>
              </div>
              <p className="max-w-sm text-sm text-muted-foreground">Nền tảng điều phối, quản trị và giám sát AI dành cho môi trường doanh nghiệp.</p>
            </div>
            <div>
              <h4 className="mb-4 text-sm font-semibold">Tài nguyên</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/help" className="transition-colors hover:text-primary">
                    {t("help_title")}
                  </Link>
                </li>
                <li>
                  <Link to="/docs" className="transition-colors hover:text-primary">
                    {t("docs_title")}
                  </Link>
                </li>
                <li>
                  <Link to="/docs/agents" className="transition-colors hover:text-primary">
                    Đặc tả Agents
                  </Link>
                </li>
                <li>
                  <Link to="/docs/hardening" className="font-bold text-primary/80 transition-colors hover:text-primary">
                    Báo cáo sẵn sàng
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 text-sm font-semibold">Pháp lý</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/privacy" className="transition-colors hover:text-primary">
                    {t("privacy_title")}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="transition-colors hover:text-primary">
                    {t("terms_title")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-4 border-t pt-8 text-xs text-muted-foreground md:flex-row">
            <p>© 2026 STV Enterprise Solution. Bảo lưu mọi quyền.</p>
            <p>{t("last_updated")}: 10/03/2026</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
